import React, { useState, useEffect } from 'react';
import { ArrowLeft, Edit, Trash2, RefreshCw, AlertCircle, Mail, Phone, User, CheckCircle, XCircle, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { ContactSubmission, ContactSubmissionStatus, UpdateContactSubmissionStatusRequest } from '../../services/admin/adminService';

const ContactSubmissionDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [submission, setSubmission] = useState<ContactSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState<UpdateContactSubmissionStatusRequest>({
    id: 0,
    status: 'pending',
    notes: '',
  });

  useEffect(() => {
    if (id) {
      fetchSubmission();
    }
  }, [id]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const data = await adminService.getContactSubmissionById(Number(id));
      setSubmission(data);
      setStatusForm({
        id: data.id,
        status: data.status,
        notes: data.notes || '',
      });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch contact submission');
      navigate('/admin/contact-submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!submission) return;
    
    try {
      setDeleting(true);
      await adminService.deleteContactSubmission(submission.id);
      toast.success('Contact submission deleted successfully!');
      navigate('/admin/contact-submissions');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete contact submission');
    } finally {
      setDeleting(false);
      setDeleteModal(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!submission) return;

    try {
      setUpdating(true);
      const updated = await adminService.updateContactSubmissionStatus(statusForm);
      setSubmission(updated);
      toast.success('Status updated successfully!');
      setStatusModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Contact submission not found</p>
        </div>
      </div>
    );
  }

  const isRead = Boolean(submission.is_read);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/contact-submissions')}
            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} className="text-secondary-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900">Contact Submission Details</h1>
            <p className="text-secondary-600 mt-1">View and manage contact form submission</p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setStatusModal(true)}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Edit size={18} />
            Update Status
          </button>
          <button
            onClick={() => setDeleteModal(true)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      {/* Submission Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
              <Mail size={32} className="text-primary-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-secondary-900">{submission.name}</h2>
              <p className="text-secondary-600">{submission.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`px-4 py-2 text-sm rounded-full font-semibold ${
                submission.status === 'resolved'
                  ? 'bg-green-100 text-green-800'
                  : submission.status === 'in_progress'
                  ? 'bg-blue-100 text-blue-800'
                  : submission.status === 'closed'
                  ? 'bg-gray-100 text-gray-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {submission.status === 'in_progress' ? 'In Progress' : submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
            </span>
            {isRead ? (
              <span className="px-3 py-1 text-xs rounded-full font-semibold bg-green-100 text-green-800 flex items-center gap-1">
                <CheckCircle size={14} />
                Read
              </span>
            ) : (
              <span className="px-3 py-1 text-xs rounded-full font-semibold bg-red-100 text-red-800 flex items-center gap-1">
                <XCircle size={14} />
                Unread
              </span>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <Mail size={16} className="inline mr-2" />
              Email
            </label>
            <p className="text-secondary-900">{submission.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              <Phone size={16} className="inline mr-2" />
              Phone
            </label>
            <p className="text-secondary-900">{submission.phone}</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Subject
            </label>
            <p className="text-secondary-900 font-semibold">{submission.subject}</p>
          </div>
        </div>

        {/* Message */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-secondary-700 mb-2">
            Message
          </label>
          <div className="bg-secondary-50 p-4 rounded-lg border border-gray-200">
            <p className="text-secondary-900 whitespace-pre-wrap leading-relaxed">
              {submission.message}
            </p>
          </div>
        </div>

        {/* Admin Reply (if exists) */}
        {submission.admin_reply && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Admin Reply
            </label>
            <p className="text-secondary-900 whitespace-pre-wrap">
              {submission.admin_reply}
            </p>
            {submission.replied_by && (
              <p className="text-xs text-secondary-500 mt-2">
                Replied by: {submission.replied_by}
                {submission.replied_at && ` on ${new Date(submission.replied_at).toLocaleString()}`}
              </p>
            )}
          </div>
        )}

        {/* Notes (if exists) */}
        {submission.notes && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Internal Notes
            </label>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-secondary-900 whitespace-pre-wrap">
                {submission.notes}
              </p>
            </div>
          </div>
        )}

        {/* Technical Information */}
        <div className="pt-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Technical Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <label className="block text-secondary-600 mb-1">IP Address</label>
              <p className="text-secondary-900 font-mono">{submission.ip_address}</p>
            </div>
            <div>
              <label className="block text-secondary-600 mb-1">User Agent</label>
              <p className="text-secondary-900 text-xs break-all">{submission.user_agent}</p>
            </div>
            <div>
              <label className="block text-secondary-600 mb-1">Created At</label>
              <p className="text-secondary-900">
                {new Date(submission.created_at).toLocaleString()}
              </p>
            </div>
            {submission.updated_at && (
              <div>
                <label className="block text-secondary-600 mb-1">Updated At</label>
                <p className="text-secondary-900">
                  {new Date(submission.updated_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Update Status Modal */}
      {statusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">Update Status</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={statusForm.status}
                    onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value as ContactSubmissionStatus })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={statusForm.notes || ''}
                    onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Add internal notes..."
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleStatusUpdate}
                  disabled={updating}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Save size={18} />
                  {updating ? 'Updating...' : 'Update Status'}
                </button>
                <button
                  onClick={() => setStatusModal(false)}
                  disabled={updating}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900">Delete Submission</h3>
              </div>
              
              <p className="text-secondary-600 mb-6">
                Are you sure you want to delete the contact submission from{' '}
                <strong>{submission.name}</strong>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissionDetails;
